import { useState, useRef, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface UseAudioRecordingProps {
  userProfile?: { subscription_tier?: string } | null;
}

export function useAudioRecording({ userProfile }: UseAudioRecordingProps = {}) {
  // Toast for error messages
  const { toast } = useToast();

  // Free users: 180 seconds (3 minutes). Pro users: unlimited.
  const maxRecordingTime = userProfile?.subscription_tier === 'pro' ? Infinity : 180;

  const [text, setText] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const isRecordingRef = useRef(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [recordingStage, setRecordingStage] = useState<'idle' | 'recording' | 'processing' | 'transcribing'>('idle');

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const recordingStartTimeRef = useRef<number | null>(null);
  const totalTimeIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Transcribe audio blob to text using Supabase Edge Function
  // Returns the transcribed text.
  const processAudio = useCallback(async (audioBlob: Blob): Promise<string> => {
    setRecordingStage('transcribing');
    setIsProcessing(true);

    try {
      const base64Audio = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          try {
            const result = reader.result as string;
            resolve(result.split(',')[1]);
          } catch (e) {
            reject(e);
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(audioBlob);
      });

      const { data, error } = await supabase.functions.invoke('transcribe-audio', {
        body: { audio: base64Audio }
      });

      if (error) throw error;
      if (!data?.text) throw new Error('No transcription received');

      // Successfully received transcription
      setRecordingStage('idle');
      setIsProcessing(false);
      return data.text;
    } catch (error) {
      // Handle transcription errors
      setRecordingStage('idle');
      setIsProcessing(false);
      throw error;
    }
  }, []);

  // When true, the next stopRecording call returns the transcript instead of
  // setting it on the hook's `text` state (so it doesn't also land in the
  // textarea). Reset to false after each stop.
  const sendImmediatelyRef = useRef(false);
  const transcriptResolverRef = useRef<((value: string) => void) | null>(null);

  // Stop recording, transcribe, and store text in-hook.
  // Returns the recorded audio blob (or null if nothing to stop).
  const stopRecording = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;

      if (!recorder || recorder.state === 'inactive' || !isRecordingRef.current) {
        resolve(null);
        return;
      }

      // Stop timers immediately so UI doesn't continue counting.
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      if (totalTimeIntervalRef.current) {
        clearInterval(totalTimeIntervalRef.current);
        totalTimeIntervalRef.current = null;
      }
      recordingStartTimeRef.current = null;
      // Update states
      setTimeLeft(0);
      setIsRecording(false);
      isRecordingRef.current = false;
      setRecordingStage('processing');

      recorder.onstop = async () => {
        try {
          const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });

          // Stop mic stream
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
          }

          // Transcribe
          if (audioBlob.size > 0) {
            try {
              const transcript = await processAudio(audioBlob);
              if (sendImmediatelyRef.current) {
                // Hand the transcript back to the caller without populating
                // the textarea state.
                transcriptResolverRef.current?.(transcript);
              } else {
                setText(transcript);
              }
            } catch (error) {
              console.error('Transcription error:', error);
              toast({
                title: 'Transcription Failed',
                description: 'Could not transcribe audio. Please try again.',
                variant: 'destructive',
              });
              if (sendImmediatelyRef.current) {
                transcriptResolverRef.current?.('');
              }
            }
          } else if (sendImmediatelyRef.current) {
            transcriptResolverRef.current?.('');
          }

          sendImmediatelyRef.current = false;
          transcriptResolverRef.current = null;
          setRecordingStage('idle');
          resolve(audioBlob);
        } catch (e) {
          setRecordingStage('idle');
          resolve(null);
        }
      };

      // Trigger stop (this fires `onstop` above)
      recorder.stop();
    });
  }, [processAudio, toast]);

  // Start recording audio
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        }
      });

      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      // Clear any previous timers before starting fresh
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      if (totalTimeIntervalRef.current) {
        clearInterval(totalTimeIntervalRef.current);
        totalTimeIntervalRef.current = null;
      }

      setText("");

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      isRecordingRef.current = true;
      setRecordingStage('recording');
      setTimeLeft(Number.isFinite(maxRecordingTime) ? maxRecordingTime : 0);
      setTotalTime(0);

      // Hard auto-stop after max recording time (only for free users).
      // Guarantees auto-stop even if the interval is throttled.
      if (Number.isFinite(maxRecordingTime)) {
        timerRef.current = setTimeout(() => {
          stopRecording();
        }, maxRecordingTime * 1000);
      }

      // Countdown display only for free users.
      if (Number.isFinite(maxRecordingTime)) {
        countdownRef.current = setInterval(() => {
          setTimeLeft((prev) => {
            // When we reach the last second, stop recording and clamp to 0.
            if (prev <= 1) {
              // Side-effect is safe because `stopRecording` is guarded by `isRecordingRef`.
              stopRecording();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }

      // Start counter for total time
      recordingStartTimeRef.current = Date.now();
      totalTimeIntervalRef.current = setInterval(() => {
        setTotalTime((prev) => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Error accessing microphone:', error);
      throw new Error('Could not access microphone. Please check permissions.');
    }
  }, [maxRecordingTime, stopRecording]);

  // Cancel recording without processing or saving
  const cancelRecording = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    if (totalTimeIntervalRef.current) {
      clearInterval(totalTimeIntervalRef.current);
      totalTimeIntervalRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }

    setIsRecording(false);
    isRecordingRef.current = false;
    setIsProcessing(false);
    setRecordingStage('idle');
    setTimeLeft(Number.isFinite(maxRecordingTime) ? maxRecordingTime : 0);
  }, [maxRecordingTime]);

  // Update timeLeft when maxRecordingTime changes (when user profile changes)
  useEffect(() => {
    if (!isRecording && !isProcessing) {
      setTimeLeft(0);
      setTotalTime(0);
    }
  }, [maxRecordingTime, isRecording, isProcessing]);

  // Stop recording but resolve with the transcript instead of stuffing it
  // into the textarea. Used by the "send now" arrow button.
  const stopAndReturnText = useCallback(async (): Promise<string> => {
    if (!isRecordingRef.current) return "";
    const transcriptPromise = new Promise<string>((resolve) => {
      transcriptResolverRef.current = resolve;
    });
    sendImmediatelyRef.current = true;
    await stopRecording();
    return transcriptPromise;
  }, [stopRecording]);

  return {
    text,
    isRecording,
    isProcessing,
    timeLeft,
    totalTime,
    recordingStage,
    startRecording,
    stopRecording,
    stopAndReturnText,
    cancelRecording,
  };
}